import React, { useState } from "react"

import { LockReset, Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Alert,
  Tooltip,
} from '@mui/material'

import { handleApiError, type User, usersService } from "../../api"

type ResetPasswordDialogProps = {
  user: User
}

export function ResetPasswordDialog({ user }: ResetPasswordDialogProps) {
  const [open, setOpen] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    setOpen(true)
    setNewPassword("")
    setConfirmPassword("")
    setError("")
    setSuccess("")
  }

  const handleClose = () => {
    if (!isLoading) {
      setOpen(false)
      setNewPassword("")
      setConfirmPassword("")
      setError("")
      setSuccess("")
    }
  }

  const handleSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      setError("Please fill in all fields")
      return
    }

    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters long")
      return
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match")
      return
    }

    try {
      setIsLoading(true)
      setError("")
      
      await usersService.resetPassword({
        userId: user.id,
        newPassword,
      })

      setSuccess("Password reset successfully!")
      
      // Close dialog after a short delay to show success message
      setTimeout(() => {
        handleClose()
      }, 1500)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const getPasswordStrength = (password: string): { level: number; text: string; color: string } => {
    if (!password) return { level: 0, text: "", color: "" }
    
    let score = 0
    if (password.length >= 8) score++
    if (/[a-z]/.test(password)) score++
    if (/[A-Z]/.test(password)) score++
    if (/\d/.test(password)) score++
    if (/[^A-Za-z0-9]/.test(password)) score++

    if (score < 2) return { level: 1, text: "Weak", color: "error.main" }
    if (score < 4) return { level: 2, text: "Medium", color: "warning.main" }
    return { level: 3, text: "Strong", color: "success.main" }
  }

  const passwordStrength = getPasswordStrength(newPassword)

  return (
    <>
      <Tooltip title="Reset Password">
        <Button
          variant="outlined"
          size="small"
          startIcon={<LockReset />}
          onClick={handleOpen}
          sx={{ minWidth: 'auto' }}
        >
          Reset Password
        </Button>
      </Tooltip>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockReset color="primary" />
            <Typography variant="h6" component="div">
              Reset Password for {user.firstName ? `${user.firstName} ${user.lastName}` : user.email}
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success">
                {success}
              </Alert>
            )}

            <TextField
              label="New Password"
              type={showPassword ? "text" : "password"}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              disabled={isLoading}
              helperText="Password must be at least 8 characters long"
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />

            {newPassword && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Password strength:
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: passwordStrength.color,
                    fontWeight: 'bold'
                  }}
                >
                  {passwordStrength.text}
                </Typography>
              </Box>
            )}

            <TextField
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              disabled={isLoading}
              error={Boolean(confirmPassword && newPassword !== confirmPassword)}
              helperText={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : undefined}
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={handleClose} 
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
          >
            {isLoading ? "Resetting..." : "Reset Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}